import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { wizardStateManager } from '../../utils/wizardStateManager';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Save,
  X,
  RefreshCw
} from 'lucide-react';

// Step components
import Step0_AccountSelection from './TestProfileWizard/Step0_AccountSelection';
import Step1_QuickProfileInput from './TestProfileWizard/Step1_QuickProfileInput';
import Step2_ParentInsightsGenerator from './TestProfileWizard/Step2_ParentInsightsGenerator';
import Step3_StrategicContextPreview from './TestProfileWizard/Step3_StrategicContextPreview';
import Step4_QuestionSetPreview from './TestProfileWizard/Step4_QuestionSetPreview';
import Step5_MockAnswersGenerator from './TestProfileWizard/Step5_MockAnswersGenerator';
import Step6_FollowupQuestionsGenerator from './TestProfileWizard/Step6_FollowupQuestionsGenerator';
import Step7_FinalAssessmentPreview from './TestProfileWizard/Step7_FinalAssessmentPreview';

const STEPS = [
  { number: 0, title: 'Account Selection', component: Step0_AccountSelection, icon: '👤' },
  { number: 1, title: 'Profile Input', component: Step1_QuickProfileInput, icon: '📝' },
  { number: 2, title: 'Parent Insights', component: Step2_ParentInsightsGenerator, icon: '👨‍👩‍👧‍👦' },
  { number: 3, title: 'Strategic Context', component: Step3_StrategicContextPreview, icon: '🎯' },
  { number: 4, title: 'Question Set', component: Step4_QuestionSetPreview, icon: '❓' },
  { number: 5, title: 'Mock Answers', component: Step5_MockAnswersGenerator, icon: '💬' },
  { number: 6, title: 'Follow-up Questions', component: Step6_FollowupQuestionsGenerator, icon: '🔄' },
  { number: 7, title: 'Final Assessment', component: Step7_FinalAssessmentPreview, icon: '✨' }
];

const TestProfileWizard = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();

  // Core wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    selectedAccount: null,
    profile: wizardStateManager.getTemplateProfile(),
    parentContextNotes: '',
    parentInsights: { parentA: {}, parentB: {} },
    strategicContext: null,
    questionSet: [],
    initialAnswers: {},
    followupQuestions: [],
    followupAnswers: {},
    finalAssessment: null
  });

  // UI state
  const [showLoadPrompt, setShowLoadPrompt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  // Check for saved progress on mount
  useEffect(() => {
    if (wizardStateManager.hasProgress()) {
      setShowLoadPrompt(true);
    }
  }, []);

  // Auto-save progress
  useEffect(() => {
    // Always save progress, including Step 0 (account selection)
    // This ensures loaded database data persists across page reloads
    wizardStateManager.saveProgress(currentStep, wizardData);
  }, [currentStep, wizardData]);

  // Load saved progress
  const loadSavedProgress = () => {
    const saved = wizardStateManager.loadProgress();
    if (saved) {
      setCurrentStep(saved.currentStep);
      setWizardData(saved.data);
      setShowLoadPrompt(false);
    }
  };

  // Start fresh
  const startFresh = () => {
    wizardStateManager.clearProgress();
    setShowLoadPrompt(false);
  };

  // Update wizard data
  const updateData = (key, value) => {
    setWizardData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Validate current step before proceeding
  const validateCurrentStep = () => {
    const validation = wizardStateManager.validateStepData(currentStep, wizardData);
    setErrors(validation.errors);
    return validation.valid;
  };

  // Save step data to database
  const saveStepData = async (step) => {
    const { selectedAccount, profile, parentInsights, questionSet, initialAnswers, followupQuestions, followupAnswers } = wizardData;

    if (!selectedAccount) {
      console.warn('No account selected, skipping database save');
      return { success: true };
    }

    try {
      console.log(`💾 Saving Step ${step} data to database for account:`, selectedAccount.id);

      switch (step) {
        case 1: // Save student academic profile
          // Check if profile already exists
          const { data: existingProfile } = await supabase
            .from('student_academic_profiles')
            .select('id')
            .eq('user_id', selectedAccount.id)
            .single();

          const profilePayload = {
            user_id: selectedAccount.id,
            name: profile.name,
            graduation_year: profile.graduation_year,
            grade_level: profile.grade_level || null,
            state_of_residency: profile.state_of_residency,
            high_school_name: profile.high_school_name || null,
            high_school_city: profile.high_school_city || null,
            high_school_state: profile.high_school_state || null,
            intended_major: profile.intended_major,
            intended_minor: profile.intended_minor || null,
            gpa_weighted: profile.gpa_weighted || null,
            gpa_unweighted: profile.gpa_unweighted || null,
            gpa_scale: profile.gpa_scale || 4.0,
            class_rank: profile.class_rank || null,
            class_size: profile.class_size || null,
            sat_total: profile.sat_total || null,
            sat_math: profile.sat_math || null,
            sat_reading_writing: profile.sat_reading_writing || null,
            act_composite: profile.act_composite || null,
            act_math: profile.act_math || null,
            act_english: profile.act_english || null,
            act_reading: profile.act_reading || null,
            act_science: profile.act_science || null,
            ap_courses_taken: profile.ap_courses_taken || 0,
            honors_courses: profile.honors_courses || 0,
            dual_enrollment_credits: profile.dual_enrollment_credits || 0,
            curriculum_rigor: profile.curriculum_rigor || null,
            academic_interests: profile.academic_interests || [],
            career_goals: profile.career_goals || '',
            special_talents: profile.special_talents || '',
            extracurriculars: profile.activities || profile.extracurriculars || [],
            awards: profile.honors_awards || profile.honors || [],
            updated_at: new Date().toISOString()
          };

          let profileData, profileError;

          if (existingProfile) {
            // Update existing profile
            const result = await supabase
              .from('student_academic_profiles')
              .update(profilePayload)
              .eq('user_id', selectedAccount.id)
              .select()
              .single();
            profileData = result.data;
            profileError = result.error;
          } else {
            // Insert new profile
            const result = await supabase
              .from('student_academic_profiles')
              .insert(profilePayload)
              .select()
              .single();
            profileData = result.data;
            profileError = result.error;
          }

          if (profileError) throw profileError;
          console.log('✅ Student profile saved:', profileData);
          break;

        case 2: // Save parent observations
          // Map parent insights to individual columns in the schema
          const parentAData = parentInsights.parentA || {};
          const parentBData = parentInsights.parentB || {};

          const { error: parentError } = await supabase
            .from('student_academic_profiles')
            .update({
              // Parent A observations
              parent_observation_easy_subject: parentAData.easy_subject || null,
              parent_observation_growth: parentAData.growth || null,
              parent_observation_learning_difference: parentAData.learning_difference || null,
              parent_observation_surprises: parentAData.surprises || null,
              parent_observation_age_limit: parentAData.age_limit || null,
              parent_observation_rules_questioned: parentAData.rules_questioned || null,
              parent_observation_wrong_level: parentAData.wrong_level || null,
              parent_observation_advocacy_success: parentAData.advocacy_success || null,
              parent_observation_initiative_against_advice: parentAData.initiative_against_advice || null,
              parent_observation_mechanical_skills: parentAData.mechanical_skills || null,
              parent_observation_math_integration: parentAData.math_integration || null,
              parent_observation_entrepreneurial_instincts: parentAData.entrepreneurial_instincts || null,
              parent_observation_obsessions: parentAData.obsessions || null,
              parent_observation_unusual_interests: parentAData.unusual_interests || null,
              parent_observation_hidden_qualities: parentAData.hidden_qualities || null,

              // Parent B observations
              parent_b_easy_subject: parentBData.easy_subject || null,
              parent_b_growth: parentBData.growth || null,
              parent_b_learning_difference: parentBData.learning_difference || null,
              parent_b_surprises: parentBData.surprises || null,
              parent_b_age_limit: parentBData.age_limit || null,
              parent_b_rules_questioned: parentBData.rules_questioned || null,
              parent_b_wrong_level: parentBData.wrong_level || null,
              parent_b_advocacy_success: parentBData.advocacy_success || null,
              parent_b_initiative_against_advice: parentBData.initiative_against_advice || null,
              parent_b_mechanical_skills: parentBData.mechanical_skills || null,
              parent_b_math_integration: parentBData.math_integration || null,
              parent_b_entrepreneurial_instincts: parentBData.entrepreneurial_instincts || null,
              parent_b_obsessions: parentBData.obsessions || null,
              parent_b_unusual_interests: parentBData.unusual_interests || null,
              parent_b_hidden_qualities: parentBData.hidden_qualities || null,
              parent_b_different_perspective: parentBData.different_perspective || null,
              parent_b_what_other_parent_misses: parentBData.what_other_parent_misses || null,

              updated_at: new Date().toISOString()
            })
            .eq('user_id', selectedAccount.id);

          if (parentError) throw parentError;
          console.log('✅ Parent observations saved');
          break;

        case 3: // Save strategic context
          // Strategic context will be saved in Step 3 component
          console.log('✅ Strategic context handled in Step 3 component');
          break;

        case 4:
        case 5:
        case 6: // Save questions and answers progressively
          console.log('✅ Questions/answers will be saved in final assessment');
          break;

        case 7: // Final assessment
          console.log('✅ Final assessment ready for generation');
          break;

        default:
          console.log(`Step ${step} - no database save required`);
      }

      return { success: true };
    } catch (error) {
      console.error(`Error saving Step ${step} data:`, error);
      return { success: false, error };
    }
  };

  // Navigate to next step
  const goToNextStep = async () => {
    if (validateCurrentStep()) {
      // Save current step data before proceeding
      setSaving(true);
      const saveResult = await saveStepData(currentStep);
      setSaving(false);

      if (!saveResult.success) {
        setErrors([`Failed to save data: ${saveResult.error?.message || 'Unknown error'}`]);
        return;
      }

      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
        setErrors([]);
      }
    }
  };

  // Navigate to previous step
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors([]);
    }
  };

  // Check if we can skip to assessment (all data is present)
  const canSkipToAssessment = () => {
    return (
      wizardData.selectedAccount &&
      wizardData.profile?.name &&
      wizardData.parentInsights?.parentA &&
      Object.keys(wizardData.parentInsights.parentA).length > 0 &&
      wizardData.strategicContext?.summary &&
      wizardData.questionSet?.length >= 8 &&
      wizardData.initialAnswers &&
      Object.keys(wizardData.initialAnswers).length >= 8 &&
      wizardData.followupQuestions?.length >= 7 &&
      wizardData.followupAnswers &&
      Object.keys(wizardData.followupAnswers).length >= 7
    );
  };

  // Jump directly to assessment step
  const jumpToAssessment = () => {
    setCurrentStep(7);
    setErrors([]);
  };

  // Save and exit
  const saveAndExit = async () => {
    setSaving(true);
    try {
      wizardStateManager.saveProgress(currentStep, wizardData);
      setTimeout(() => {
        navigate('/college');
      }, 500);
    } catch (error) {
      console.error('Error saving wizard progress:', error);
    } finally {
      setSaving(false);
    }
  };

  // Get progress percentage
  const getProgressPercentage = () => {
    return Math.round(((currentStep + 1) / STEPS.length) * 100);
  };

  // Current step component
  const CurrentStepComponent = STEPS[currentStep]?.component;

  if (!CurrentStepComponent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Invalid Step</h2>
          <p className="text-slate-600 mb-4">Step {currentStep} not found.</p>
          <button
            onClick={() => setCurrentStep(0)}
            className="px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700"
          >
            Return to Start
          </button>
        </div>
      </div>
    );
  }

  // Load prompt modal
  if (showLoadPrompt) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-azure-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-8 h-8 text-azure-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Continue Previous Session?</h2>
            <p className="text-slate-600">
              We found a saved test profile wizard session. Would you like to continue where you left off?
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={loadSavedProgress}
              className="w-full px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 font-medium transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Continue Previous Session
            </button>
            <button
              onClick={startFresh}
              className="w-full px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
            >
              Start Fresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-azure-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/college')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Exit Wizard
            </button>

            <div className="flex items-center gap-2">
              {canSkipToAssessment() && currentStep !== 7 && (
                <button
                  onClick={jumpToAssessment}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium"
                  title="All data is present - jump to assessment generation"
                >
                  ✨ Generate Assessment
                </button>
              )}
              <button
                onClick={saveAndExit}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save & Exit'}
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold text-slate-900">Test Profile Wizard</h1>
              <span className="text-sm font-medium text-slate-600">
                Step {currentStep + 1} of {STEPS.length} ({getProgressPercentage()}%)
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-azure-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.number}>
                <div
                  className={`flex flex-col items-center ${
                    step.number === currentStep
                      ? 'scale-110'
                      : step.number < currentStep
                      ? 'opacity-70'
                      : 'opacity-40'
                  } transition-all duration-200`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                      step.number < currentStep
                        ? 'bg-emerald-500 text-white'
                        : step.number === currentStep
                        ? 'bg-azure-600 text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {step.number < currentStep ? <Check className="w-5 h-5" /> : step.icon}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${
                    step.number === currentStep ? 'text-azure-900' : 'text-slate-600'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      step.number < currentStep ? 'bg-emerald-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Validation errors */}
        {errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">Please fix the following errors:</h3>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setErrors([])}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Current step component */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          <CurrentStepComponent
            wizardData={wizardData}
            updateData={updateData}
            setWizardData={setWizardData}
            goToNextStep={goToNextStep}
            currentUser={currentUser}
          />
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={goToPreviousStep}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex items-center gap-3">
            {currentStep < STEPS.length - 1 && (
              <button
                onClick={goToNextStep}
                className="flex items-center gap-2 px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors font-medium"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            )}

            {currentStep === STEPS.length - 1 && (
              <button
                onClick={() => {
                  wizardStateManager.clearProgress();
                  navigate('/college');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Check className="w-4 h-4" />
                Complete & Exit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestProfileWizard;
